import {
  addError,
  addLoading,
  getLoading,
  removeLoadingAndError,
} from "../../core/redux/api/module";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../../core/redux/api/circuitBreaker/module";
import { apiGetGuestHydrationErrorsCount } from "../../core/redux/api/endpoints";
import { IAppState } from "../../core/redux/IAppState";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../../core/redux/api/reduxStandardApiAction";
import { ACTION_TYPE } from "../../core/redux/actionType";
import { getResource } from "../../language/resources/getResource";
import * as R from "ramda";
import { isNilOrEmpty } from "../../language/module";

const KEY_ACTION = ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_COUNT_REQUEST;
export const ACTIONS = [
  ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_COUNT_REQUEST,
  ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_COUNT_RESPONSE,
  ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_COUNT_ERROR,
];

/**
 * Makes an action that can be dispatched to get determine if a resort has product mapping error
 */
export const getGuestHydrationErrorsCountAction = (
  resortAbbreviation: string
) =>
  makeApiAction({
    method: "GET",
    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: apiGetGuestHydrationErrorsCount(resortAbbreviation),
    types: ACTIONS.map((action) => ({
      type: action,
      meta: { resortAbbreviation },
    })) as RsaaTypes,
    // Bail if already loading results for that query for that resort
    bailout: (state: IAppState) =>
      hasTrippedBreaker(state, KEY_ACTION) ||
      getLoading(state, resortAbbreviation, KEY_ACTION)?.requestId ===
        resortAbbreviation,
  });

const reducer: ApiReducer<boolean> = (
  state,
  action,
  error,
  isLoading,
  payload
): IAppState => {
  const resort = action.meta.resortAbbreviation;
  const appStateResort = R.path<string>(
    ["selectedResort", "resortAbbreviation"],
    state
  );

  // Ignore the results if the response is for a previous query that didn't complete before the user switched resorts
  if (
    action.type === ACTION_TYPE.RESORT_MAPPING_HAS_ERROR_RESPONSE &&
    !isNilOrEmpty(appStateResort) &&
    appStateResort !== resort
  ) {
    const withBreaker = updateBreaker(state, KEY_ACTION, !!error);
    return removeLoadingAndError(withBreaker, resort, KEY_ACTION);
  }

  if (isLoading) {
    return addLoading(state, resort, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      resort,
      KEY_ACTION,
      getResource(state.locale, "GET_GUEST_HYDRATION_ERRORS_COUNT_ACTION_ERROR")
    );
  }

  // Remove loading/error since we're not loading or erroring
  if (payload === undefined) {
    return state;
  }
  const noLoadingOrError = removeLoadingAndError(
    withBreaker,
    resort,
    KEY_ACTION
  );

  return R.assocPath(
    ['guestHydrationErrorReportCount'],
    payload,
    noLoadingOrError
  );
};

/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to a guest's redemptions at resorts while using a specific pass. Returns a new app state, of course.
 */
export const getGuestHydrationErrorsCountReducer = makeApiReducer<boolean>(
  ACTIONS,
  KEY_ACTION,
  reducer
);
